/**
 * `docTemplatesApi` — RTK Query slice for the Documents page's
 * **templates** tab (folder explorer + uploaded template files). Routed
 * through the `axilon-api-service` BFF under `/api/ontology/report-*`
 * (`doc-templates-routes.ts` → `ProxyReportTemplatesClient`), which fronts the
 * proxy-server `report_template_folders` / `report_templates` tables.
 *
 * Mirrors `templatesApi` (its own cache + tag space) but the leaf rows
 * are binary files: the upload mutation posts `multipart/form-data`
 * (RTK's `fetchBaseQuery` forwards a `FormData` body untouched, letting
 * the browser set the boundary). Downloads are not RTK endpoints — see
 * `download.ts`, which streams the file with auth headers and saves it.
 */

import { createApi } from '@reduxjs/toolkit/query/react'

import { getOntologyServiceBaseUrl } from '../../config'
import { ontologyBaseQuery } from '../core'
import type {
  CopyDocTemplateFolderInput,
  CopyDocTemplateFolderResponse,
  CopyDocTemplateInput,
  CreateDocTemplateFolderInput,
  DocTemplate,
  DocTemplateFields,
  DocTemplateFolder,
  DocTemplatePreview,
  MoveDocTemplateFolderInput,
  MoveDocTemplateFolderResponse,
  PatchDocTemplateInput,
  RenameDocTemplateFolderInput,
  UploadedDocTemplate,
  UploadDocTemplateArgs
} from './types'

const ONTOLOGY_SERVICE_URL = getOntologyServiceBaseUrl()

export const DOC_TEMPLATES_TAG_TYPES = ['DocTemplateFolder', 'DocTemplate'] as const
export type DocTemplatesTagType = (typeof DOC_TEMPLATES_TAG_TYPES)[number]

export const docTemplatesApi = createApi({
  reducerPath: 'docTemplatesApi',
  baseQuery: ontologyBaseQuery,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: DOC_TEMPLATES_TAG_TYPES,
  endpoints: (builder) => ({
    listDocTemplateFolders: builder.query<DocTemplateFolder[], void>({
      query: () => `${ONTOLOGY_SERVICE_URL}/report-template-folders`,
      transformResponse: (response: { folders?: DocTemplateFolder[] }) => response.folders ?? [],
      providesTags: ['DocTemplateFolder']
    }),
    createDocTemplateFolder: builder.mutation<DocTemplateFolder, CreateDocTemplateFolderInput>({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/report-template-folders`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['DocTemplateFolder']
    }),
    renameDocTemplateFolder: builder.mutation<
      DocTemplateFolder,
      { id: string; input: RenameDocTemplateFolderInput }
    >({
      query: ({ id, input }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/report-template-folders/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: input
      }),
      invalidatesTags: ['DocTemplateFolder']
    }),
    deleteDocTemplateFolder: builder.mutation<void, string>({
      query: (id) => ({
        url: `${ONTOLOGY_SERVICE_URL}/report-template-folders/${encodeURIComponent(id)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['DocTemplateFolder', 'DocTemplate']
    }),
    moveDocTemplateFolder: builder.mutation<
      MoveDocTemplateFolderResponse,
      MoveDocTemplateFolderInput
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/report-template-folders/move`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['DocTemplateFolder', 'DocTemplate']
    }),
    copyDocTemplateFolder: builder.mutation<
      CopyDocTemplateFolderResponse,
      CopyDocTemplateFolderInput
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/report-template-folders/copy`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['DocTemplateFolder', 'DocTemplate']
    }),
    deleteDocTemplateFolderByPath: builder.mutation<void, { path: string }>({
      query: ({ path }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/report-template-folders/delete`,
        method: 'POST',
        body: { path }
      }),
      invalidatesTags: ['DocTemplateFolder', 'DocTemplate']
    }),

    listDocTemplates: builder.query<DocTemplate[], void>({
      query: () => `${ONTOLOGY_SERVICE_URL}/report-templates`,
      transformResponse: (response: { templates?: DocTemplate[] }) => response.templates ?? [],
      providesTags: ['DocTemplate']
    }),
    getDocTemplateFields: builder.query<DocTemplateFields, string>({
      query: (id) => `${ONTOLOGY_SERVICE_URL}/report-templates/${encodeURIComponent(id)}/fields`,
      providesTags: (_result, _error, id) => [{ type: 'DocTemplate', id }]
    }),
    getDocTemplatePreview: builder.query<DocTemplatePreview, string>({
      query: (id) => `${ONTOLOGY_SERVICE_URL}/report-templates/${encodeURIComponent(id)}/preview`,
      providesTags: (_result, _error, id) => [{ type: 'DocTemplate', id }]
    }),
    uploadDocTemplate: builder.mutation<UploadedDocTemplate, UploadDocTemplateArgs>({
      query: ({ file, name, path, autoAddFields }) => {
        const form = new FormData()
        form.append('file', file)
        if (name && name.length > 0) form.append('name', name)
        form.append('path', path)
        if (autoAddFields) form.append('autoAddFields', 'true')
        return {
          url: `${ONTOLOGY_SERVICE_URL}/report-templates`,
          method: 'POST',
          body: form
        }
      },
      invalidatesTags: ['DocTemplate', 'DocTemplateFolder']
    }),
    renameDocTemplate: builder.mutation<DocTemplate, { id: string; input: PatchDocTemplateInput }>({
      query: ({ id, input }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/report-templates/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: input
      }),
      invalidatesTags: ['DocTemplate', 'DocTemplateFolder']
    }),
    copyDocTemplate: builder.mutation<DocTemplate, { id: string; input: CopyDocTemplateInput }>({
      query: ({ id, input }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/report-templates/${encodeURIComponent(id)}/copy`,
        method: 'POST',
        body: input
      }),
      invalidatesTags: ['DocTemplate', 'DocTemplateFolder']
    }),
    deleteDocTemplate: builder.mutation<void, string>({
      query: (id) => ({
        url: `${ONTOLOGY_SERVICE_URL}/report-templates/${encodeURIComponent(id)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['DocTemplate', 'DocTemplateFolder']
    })
  })
})

export const {
  useListDocTemplateFoldersQuery,
  useCreateDocTemplateFolderMutation,
  useRenameDocTemplateFolderMutation,
  useDeleteDocTemplateFolderMutation,
  useMoveDocTemplateFolderMutation,
  useCopyDocTemplateFolderMutation,
  useDeleteDocTemplateFolderByPathMutation,
  useListDocTemplatesQuery,
  useGetDocTemplateFieldsQuery,
  useLazyGetDocTemplateFieldsQuery,
  useGetDocTemplatePreviewQuery,
  useUploadDocTemplateMutation,
  useRenameDocTemplateMutation,
  useCopyDocTemplateMutation,
  useDeleteDocTemplateMutation
} = docTemplatesApi
